import { useMemo, useState, type ReactNode } from 'react';
import { Archive, RotateCcw, Search } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { QueryError } from '@/components/ui/QueryError';
import { Skeleton } from '@/components/ui/Skeleton';
import type { ApiQueryState } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { ApiRequestError } from '@/lib/apiClient';
import { formatDate } from '@/lib/format';
import type { SuspendedClass, SuspendedSection } from '@/types/instituteClass';
import { classesApi } from '../../api/classes.api';

export interface SuspendedClassesListProps {
  /** Without `CLASS_MANAGE` the list is read-only. */
  canManage: boolean;
  /**
   * Read by the page rather than here, so the Suspended tab can show its count
   * before it is ever opened.
   */
  classes: ApiQueryState<SuspendedClass[]>;
  sections: ApiQueryState<SuspendedSection[]>;
  /** Raised once a class or section is back, so the roster can re-read. */
  onReinstated: () => void;
}

/**
 * The tab's search, in the address bar beside the overview's own filters, so
 * leaving the Suspended tab and coming back finds it still typed. Prefixed:
 * the classes page already uses `search` for the overview's.
 */
type Filters = { search: string };

const DEFAULT_FILTERS: Filters = { search: '' };

const FILTER_OPTIONS = { prefix: 'suspended' };

/** Whether any of a row's names contains the search, ignoring case. */
function matchesSearch(needle: string, values: (string | null)[]): boolean {
  return !needle || values.some((value) => (value ?? '').toLowerCase().includes(needle));
}

/**
 * The classes the institute has suspended, and the way back to each — the
 * classes page's Suspended tab.
 *
 * Suspending keeps the record — the dialog says so — and this is where that
 * promise is kept: every suspended class with the sections that went with it,
 * and a Reactivate that brings back the class, its sections, subjects and fees
 * together. Below them, the sections suspended on their own from a class that
 * still runs — 10th B taken off while 10th A carries on — each with its own
 * Reactivate.
 */
export function SuspendedClassesList({ canManage, classes, sections, onReinstated }: SuspendedClassesListProps) {
  // Its own search: the overview's filter bar narrows by teacher, session and
  // stage, none of which a suspended entry has.
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const { search } = filters;
  const error = classes.error ?? sections.error;
  const isLoading = (classes.isLoading && !classes.data) || (sections.isLoading && !sections.data);

  const needle = search.trim().toLowerCase();
  const shownClasses = useMemo(
    () => (classes.data ?? []).filter((row) => matchesSearch(needle, [row.name, row.code])),
    [classes.data, needle],
  );
  const shownSections = useMemo(
    () =>
      (sections.data ?? []).filter((row) =>
        matchesSearch(needle, [row.class.name, row.class.code, `${row.class.name} - ${row.name}`]),
      ),
    [sections.data, needle],
  );
  const [reinstatingUuid, setReinstatingUuid] = useState<string | null>(null);

  function refetchBoth() {
    classes.refetch();
    sections.refetch();
  }

  /**
   * One path for both kinds. Both lists are re-read either way: a class coming
   * back can uncover a section suspended on its own before the class went.
   */
  async function reinstate(uuid: string, label: string, request: () => Promise<unknown>, done: string) {
    setReinstatingUuid(uuid);
    try {
      await request();
      toast.success(`${label} is back`, done);
      refetchBoth();
      onReinstated();
    } catch (caught) {
      toast.error(
        `Could not reactivate ${label}`,
        caught instanceof ApiRequestError ? caught.message : 'Please try again.',
      );
    } finally {
      setReinstatingUuid(null);
    }
  }

  function reinstateClass(row: SuspendedClass) {
    void reinstate(
      row.uuid,
      row.name,
      () => classesApi.reinstate(row.uuid),
      'It is offered on admissions and fee plans again.',
    );
  }

  function reinstateSection(row: SuspendedSection) {
    void reinstate(
      row.uuid,
      `${row.class.name} - ${row.name}`,
      () => classesApi.reinstateSection(row.uuid),
      'It takes admissions again, with the subjects it taught before.',
    );
  }

  const reactivate = (uuid: string, onClick: () => void) =>
    canManage && (
      <Button
        variant="secondary"
        size="sm"
        leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
        isLoading={reinstatingUuid === uuid}
        disabled={reinstatingUuid !== null && reinstatingUuid !== uuid}
        onClick={onClick}
      >
        Reactivate
      </Button>
    );

  const isEmpty = shownClasses.length === 0 && shownSections.length === 0;

  return (
    <section aria-label="Suspended classes" className="card flex flex-col gap-4 p-4 sm:p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-ink-2">
          Classes and sections taken off admissions and fee plans. Nothing in them was deleted.
        </p>
        <div className="w-full sm:w-64">
          <Input
            label="Search suspended classes and sections"
            hideLabel
            type="search"
            placeholder="Search by class, code or section…"
            leftIcon={<Search className="h-3.5 w-3.5" aria-hidden />}
            value={search}
            onChange={(event) => setFilters({ search: event.target.value })}
          />
        </div>
      </div>
      {error ? (
        <QueryError title="Could not load suspended classes" error={error} onRetry={refetchBoth} />
      ) : isLoading ? (
        <div className="flex flex-col gap-2" role="status" aria-label="Loading suspended classes">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-14 w-full rounded-xl" />
          ))}
        </div>
      ) : isEmpty ? (
        <EmptyState
          icon={<Archive className="h-5 w-5" />}
          title={needle ? 'Nothing suspended matches that search' : 'No suspended classes or sections'}
          description={
            needle
              ? 'Try another name or code, or clear the search.'
              : 'A class or section you suspend from Classes overview appears here, ready to be reactivated.'
          }
        />
      ) : (
        <>
          {shownClasses.length > 0 && (
            <SuspendedGroup heading="Classes">
              {shownClasses.map((row) => (
                <SuspendedRow
                  key={row.uuid}
                  title={row.name}
                  code={row.code}
                  detail={row.sections.length > 0 ? `Sections ${row.sections.join(', ')}` : 'No sections'}
                  suspendedAt={row.suspendedAt}
                  action={reactivate(row.uuid, () => reinstateClass(row))}
                />
              ))}
            </SuspendedGroup>
          )}
          {shownSections.length > 0 && (
            <SuspendedGroup heading="Sections">
              {shownSections.map((row) => (
                <SuspendedRow
                  key={row.uuid}
                  title={`${row.class.name} - ${row.name}`}
                  code={row.class.code}
                  detail={`${row.class.name}'s other sections are running`}
                  suspendedAt={row.suspendedAt}
                  action={reactivate(row.uuid, () => reinstateSection(row))}
                />
              ))}
            </SuspendedGroup>
          )}
        </>
      )}
    </section>
  );
}

/** One kind of suspended entry under its own micro-label. */
function SuspendedGroup({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <h3 className="lbl">{heading}</h3>
      <ul className="flex flex-col divide-y divide-line rounded-card border border-line">{children}</ul>
    </div>
  );
}

/** A suspended class or section: what it is, when it went, and the way back. */
function SuspendedRow({
  title,
  code,
  detail,
  suspendedAt,
  action,
}: {
  title: string;
  code: string | null;
  detail: string;
  suspendedAt: string | null;
  action: ReactNode;
}) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-bold text-ink">
          {title}
          {code && <span className="ml-2 font-mono text-xs text-ink-3">{code}</span>}
        </p>
        <p className="text-xs text-ink-3">
          {detail}
          {suspendedAt && ` · suspended ${formatDate(suspendedAt)}`}
        </p>
      </div>
      {action}
    </li>
  );
}
